import { useEffect, useRef, useState } from 'react';

const FRAME = 280;
const OUTPUT = 640;

interface HeadshotFramerProps {
  source: Blob;
  busy?: boolean;
  onCancel: () => void;
  onSave: (file: File) => void;
}

export default function HeadshotFramer({ source, busy = false, onCancel, onSave }: HeadshotFramerProps) {
  const [url, setUrl] = useState('');
  const [natural, setNatural] = useState({ width: 0, height: 0 });
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const drag = useRef<{ pointerId: number; x: number; y: number; originX: number; originY: number } | null>(null);

  useEffect(() => {
    const next = URL.createObjectURL(source);
    setUrl(next);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    return () => URL.revokeObjectURL(next);
  }, [source]);

  const cover = natural.width > 0 && natural.height > 0
    ? Math.max(FRAME / natural.width, FRAME / natural.height)
    : 1;
  const drawnWidth = natural.width * cover * zoom;
  const drawnHeight = natural.height * cover * zoom;
  const limitX = Math.max(0, (drawnWidth - FRAME) / 2);
  const limitY = Math.max(0, (drawnHeight - FRAME) / 2);
  const x = Math.min(limitX, Math.max(-limitX, offset.x));
  const y = Math.min(limitY, Math.max(-limitY, offset.y));

  function save() {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = OUTPUT;
      canvas.height = OUTPUT;
      const context = canvas.getContext('2d');
      if (!context) return;
      const ratio = OUTPUT / FRAME;
      context.drawImage(
        image,
        (OUTPUT - drawnWidth * ratio) / 2 + x * ratio,
        (OUTPUT - drawnHeight * ratio) / 2 + y * ratio,
        drawnWidth * ratio,
        drawnHeight * ratio,
      );
      canvas.toBlob((blob) => {
        if (!blob) return;
        onSave(new File([blob], 'headshot.jpg', { type: 'image/jpeg' }));
      }, 'image/jpeg', 0.92);
    };
    image.src = url;
  }

  return (
    <div>
      <div
        className="relative mx-auto mt-3 h-[280px] w-[280px] touch-none overflow-hidden rounded-xl bg-gray-900"
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          drag.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, originX: x, originY: y };
        }}
        onPointerMove={(event) => {
          const current = drag.current;
          if (!current || current.pointerId !== event.pointerId) return;
          setOffset({
            x: current.originX + event.clientX - current.x,
            y: current.originY + event.clientY - current.y,
          });
        }}
        onPointerUp={() => {
          drag.current = null;
        }}
      >
        {url ? (
          <img
            src={url}
            alt=""
            draggable={false}
            onLoad={(event) => setNatural({
              width: event.currentTarget.naturalWidth,
              height: event.currentTarget.naturalHeight,
            })}
            className="absolute max-w-none select-none"
            style={{
              width: drawnWidth || undefined,
              height: drawnHeight || undefined,
              left: (FRAME - drawnWidth) / 2 + x,
              top: (FRAME - drawnHeight) / 2 + y,
            }}
          />
        ) : null}
        <div
          className="pointer-events-none absolute inset-0 rounded-full"
          style={{ boxShadow: 'inset 0 0 0 2px white, 0 0 0 999px rgba(17, 24, 39, 0.55)' }}
        />
      </div>
      <label className="mt-3 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
        Zoom
        <input
          className="mt-1 w-full"
          type="range"
          min={1}
          max={3}
          step={0.01}
          value={zoom}
          onChange={(event) => setZoom(Number(event.target.value))}
        />
      </label>
      <div className="mt-3 flex gap-2">
        <button type="button" className="rounded-lg border border-gray-300 dark:border-white/15 px-3 py-2 text-sm" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="rounded-lg bg-gray-900 px-3 py-2 text-sm text-white disabled:opacity-40" onClick={save} disabled={busy || !url}>
          Use photo
        </button>
      </div>
    </div>
  );
}
